import { store, store$ } from '@redux/store';
import { filter, fromEvent, map, merge, distinctUntilChanged } from 'rxjs';
import { googleAnalytics4Event } from 'features/googleAnalytics4';
import typeahead from 'features/articleSearchBox';
import HousingProductType from 'constants/housingProductType';
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';

const getPopularPillElement = (event) => event.target.closest('[data-navigator-item]');
const getLinkableArticleElement = (event) => event.target.closest('[data-article-card-link]');
const getLinkableLifestyleInsightElement = (event) =>
  event.target.closest('[data-section-name="lifestyleInsights"] [data-sublanding-link]');
const isMetroSpecificSection = (event) => !!event.target.closest('[data-section-name="metroSpecificSubjects"]');
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const isMostPopularSection = (event) => !!event.target.closest('[data-section-name="mostPopularArticles"]');
const isMostRecentSection = (event) => !!event.target.closest('[data-section-name="mostRecentArticles"]');
const isWeAlsoRecommendSection = (event) => !!event.target.closest('[data-section-name="weAlsoRecommendArticles"]');
const getStandardCardElement = (event) => event.target.closest('[data-card]');
const isListingForYouSection = (event) => !!event.target.closest('[data-listings-for-you-container]');
const isFavoriteIcon = (event) => !!event.target.closest('.icon__save');
const isCallToAction = (event) => !!event.target.closest('[data-lead-generator-opener]');
const isStandardCard = (event) => !!event.target.closest('[data-standard-card]');
const isPriced = (price) => price && price !== '0';

const bodyClick$ = fromEvent(document.body, 'click');
const listingForYouSectionClick$ = bodyClick$.pipe(filter((event) => isListingForYouSection(event)));

/**
 * Dispatches a Analytics Event When an suggestion is clicked.
 * @param suggestion - is the suggestion clicked in the search box.
 */
const triggerSearchExecutedEvent = function (suggestion) {
  const customProperties = {
    search_value: suggestion.dataset.title,
    page_type: 'Learning_Center',
    click_target: 'Learn more about HBP',
  };

  store.dispatch(
    googleAnalytics4Event({
      eventName: 'searchterm_events',
      customProperties,
    }),
  );
};

typeahead.getSuggestionListClickedObserver().subscribe(triggerSearchExecutedEvent);

/**
 * Dispatches a Analytics Event event when a navigator item is clicked.
 * @param item - is the item clicked in navigator section.
 */
const triggerNavigatorItemClick = function (item) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: item.dataset.text,
    destination: item.dataset.url,
  };

  store.dispatch(googleAnalytics4Event({ eventName: 'tag_click', customProperties }));
};

// Observers to navigator item section Events Logic
const onNavigatorItemClick$ = bodyClick$.pipe(map(getPopularPillElement), filter(Boolean));

onNavigatorItemClick$.subscribe(triggerNavigatorItemClick);

/**
 * getModalClosePaidCommunityCustomProperties: function to get the custom properties of close button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidCommunityCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.priceLow,
    max_price: dataset.priceHigh,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
    is_paid: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getModalClosePaidPlanCustomProperties: function to get the custom properties of close button click
 * based on the plan listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidPlanCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    plan_id: dataset.planId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'False',
    is_plan: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getModalClosePaidSpecCustomProperties: function to get the custom properties of close button click
 * based on the spec listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalClosePaidSpecCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    spec_id: dataset.specId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'True',
    is_plan: 'False',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getRecommendedSubmittedPaidCommunityCustomProperties: function to get custom properties of Recommended button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getRecommendedSubmittedPaidCommunityCustomProperties = function (modalInformation) {
  const { selectedRecommendedCommunities } = modalInformation;
  selectedRecommendedCommunities.forEach((dataset) => {
    const customProperties = {
      page_type: 'Learning_Center',
      click_target: 'Learn Center Listings Cards',
      button_name: 'Get More Info',
      event_type: 'TDV Events',
      currency_value: 'USD',
      value: 30,
      trustbuilder_reviews: dataset.HasBuilderReviews,
      min_price: dataset.PriceLow,
      max_price: dataset.PriceHigh,
      market_id: dataset.MarketId,
      market_name: `${dataset.MarketName}, ${dataset.StateAbbreviation}`,
      community_name: dataset.CommunityName,
      community_status: dataset.MarketingStatus || 'Normal',
      community_id: dataset.CommunityId,
      price_reduction: dataset.PriceReductionLabel,
      is_priced: isPriced(dataset.PriceLow) || isPriced(dataset.PriceHigh) ? 'True' : 'False',
      is_paid: 'True',
      zip_code: dataset.Zip,
      builder_id: dataset.BuilderId,
      builder_name: dataset.BrandName,
    };

    store.dispatch(
      googleAnalytics4Event({
        eventName: 'direct_rec_lead',
        customProperties,
      }),
    );
  });
};

/**
 * getLeadButtonClickedPaidCommunityCustomProperties: function to get the custom properties of lead button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getLeadButtonClickedPaidCommunityCustomProperties = function (standarCommunityCard) {
  const dataset = standarCommunityCard.dataset;
  const destinationUrl = dataset?.url ?? standarCommunityCard?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.priceLow,
    max_price: dataset.priceHigh,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
    is_paid: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getLeadButtonClickedPaidPlanCustomProperties: function to get the custom properties of lead button click
 * based on the plan listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getLeadButtonClickedPaidPlanCustomProperties = function (standarPlanCard) {
  const dataset = standarPlanCard.dataset;
  const destinationUrl = dataset?.url ?? standarPlanCard?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    plan_id: dataset.planId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'False',
    is_plan: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getLeadButtonClickedPaidSpecCustomProperties: function to get the custom properties of lead button click
 * based on the spec listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getLeadButtonClickedPaidSpecCustomProperties = function (specStandardCard) {
  const dataset = specStandardCard.dataset;
  const destinationUrl = dataset?.url ?? specStandardCard?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    spec_id: dataset.specId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'True',
    is_plan: 'False',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getModalSubmittedPaidCommunityCustomProperties: function to get the custom properties of submitted button click
 * based on the community listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalSubmittedPaidCommunityCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    event_type: 'TDV Events',
    currency_value: 'USD',
    value: 30,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.priceLow,
    max_price: dataset.priceHigh,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
    is_paid: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getModalSubmittedPaidPlanCustomProperties: function to get the custom properties of Submitted button click
 * based on the plan listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalSubmittedPaidPlanCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    event_type: 'TDV Events',
    currency_value: 'USD',
    value: 30,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    plan_id: dataset.planId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'False',
    is_plan: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getModalSubmittedPaidSpecCustomProperties: function to get the custom properties of Submitted button click
 * based on the spec listing card information
 * @param {object} data object with the information of the event
 * @returns custom properties object
 */
const getModalSubmittedPaidSpecCustomProperties = function (data) {
  const dataset = data.listingInformation;
  const destinationUrl = data.url;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    button_name: 'Download Brochure',
    event_type: 'TDV Events',
    currency_value: 'USD',
    value: 30,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    spec_id: dataset.specId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'True',
    is_plan: 'False',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getAreaClickedName: function to get the click target event based on the element clicked
 * @param {HTMLElement} element target element
 * @returns an string object
 */
const getAreaClickedName = function (element) {
  const areaClickedPerCardElement = {
    image: 'Listings Image Clicked',
    communityName: 'Listing text link',
    planName: 'Listing text link',
    specAddress: 'Listing text link',
    favoriteButton: 'Listing favorite',
    default: 'Card Clicked',
  };

  const elementName = element.dataset.cardElement;
  return areaClickedPerCardElement[elementName] || areaClickedPerCardElement.default;
};

/**
 * getPaidCommunityCardProperties : function to return the respective custom properties
 * @param {object} dataset from the standard community card element
 * @returns custom properties object
 */
const getPaidCommunityCardProperties = function (data) {
  const dataset = data.card.dataset;
  const destinationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    click_target1: data.clickTarget,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.priceLow,
    max_price: dataset.priceHigh,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.priceLow) || isPriced(dataset.priceHigh) ? 'True' : 'False',
    is_paid: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getPaidPlanCardProperties: function to return the respective custom properties
 * @param {object} dataset from the standard plan card element
 * @returns custom properties object
 */
const getPaidPlanCardProperties = function (data) {
  const dataset = data.card.dataset;
  const destinationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    click_target1: data.clickTarget,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    plan_id: dataset.planId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'False',
    is_plan: 'True',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * getPaidSpecCardProperties: function to return the respective custom properties
 * @param {object} data from the standard plan card element
 * @returns custom properties object
 */
const getPaidSpecCardProperties = function (data) {
  const dataset = data.card.dataset;
  const destinationUrl = dataset?.url ?? data.card?.querySelector('a')?.href;

  const customProperties = {
    page_type: 'Learning_Center',
    click_target: 'Learn Center Listings Cards',
    click_target1: data.clickTarget,
    destination: destinationUrl,
    trustbuilder_reviews: dataset.hasBuilderReviews,
    min_price: dataset.price,
    market_id: dataset.marketId,
    market_name: `${dataset.marketName}, ${dataset.stateAbbreviation}`,
    community_name: dataset.communityName,
    community_id: dataset.communityId,
    spec_id: dataset.specId,
    home_status: dataset.homeStatus,
    position: dataset.index,
    price_reduction: dataset.priceReductionLabel,
    is_priced: isPriced(dataset.price) ? 'True' : 'False',
    is_paid: 'True',
    is_spec: 'True',
    is_plan: 'False',
    zip_code: dataset.zip,
    builder_id: dataset.builderId,
    builder_name: dataset.brandName,
  };

  return customProperties;
};

/**
 * Dispatches a Analytics Event When a listing is clicked.
 * @param event - event information needed.
 */
const triggerListingCardClick = function (event) {
  store.dispatch(
    googleAnalytics4Event({
      eventName: event.name,
      customProperties: event.customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event When the call to action is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerLeadButtonClickedEvent = function (customProperties) {
  store.dispatch(
    googleAnalytics4Event({
      eventName: 'form_open',
      customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event When the Submitt Lead Button is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerModalSubmittedEvent = function (customProperties) {
  store.dispatch(
    googleAnalytics4Event({
      eventName: 'generate_lead',
      customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event When the modal is closed.
 * @param customProperties - is the custom properties of the event.
 */
const triggermodalCloseEvent = function (customProperties) {
  store.dispatch(
    googleAnalytics4Event({
      eventName: 'form_close',
      customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event event when a sub landing page card is clicked.
 * @param subLanding - is the sublanding clicked in Lifestyle Insights section.
 */
const triggerLifestyleInsightClick = function (subLanding) {
  const customProperties = {
    page_type: 'Learning_Center',
    click_target: subLanding.dataset.title,
    destination: subLanding.dataset.url,
    position: subLanding.dataset.index,
  };

  store.dispatch(
    googleAnalytics4Event({
      eventName: 'sub_landing_page_click',
      customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event event recommendation article is clicked.
 * @param article - is the article clicked in recommendation articles section.
 */
const triggerRecomendedArticlesClick = function (article) {
  const customProperties = {
    recommendation_type: 'similar articles',
    content_id: article.dataset.slug,
    content_title: article.dataset.title,
    page_type: 'Learning_Center',
  };

  store.dispatch(
    googleAnalytics4Event({
      eventName: 'recommendation_click',
      customProperties,
    }),
  );
};

/**
 * Dispatches a Analytics Event When an article is clicked.
 * @param data - is the data needed to map the click event in the articles section.
 */
const triggerArticleClick = function (data) {
  const article = data.article;
  const clickTarget = `${data.sectionName} ${article.dataset.target}`;
  const customProperties = {
    article_id: article.dataset.slug,
    article_title: article.dataset.title,
    page_type: 'Learning_Center',
    click_target: clickTarget,
    destination: article.dataset.url,
    position: article.dataset.index,
  };

  store.dispatch(
    googleAnalytics4Event({
      eventName: 'article_click',
      customProperties,
    }),
  );
};

// Observers to Life Style Section Events Logic
const onLifestyleInsightClick$ = bodyClick$.pipe(map(getLinkableLifestyleInsightElement), filter(Boolean));

onLifestyleInsightClick$.subscribe(triggerLifestyleInsightClick);

// Observers to Most Popular Section Events Logic
const onMostPopularArticlesClick$ = bodyClick$.pipe(
  filter(isMostPopularSection),
  map(getLinkableArticleElement),
  filter(Boolean),
);

onMostPopularArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to Most Recent Section Events Logic
const onMostRecentArticlesClick$ = bodyClick$.pipe(
  filter(isMostRecentSection),
  map(getLinkableArticleElement),
  filter(Boolean),
);

onMostRecentArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to We Also Recommend Section Section Events Logic
const onWeAlsoRecommendArticlesClick$ = bodyClick$.pipe(
  filter(isWeAlsoRecommendSection),
  map(getLinkableArticleElement),
  filter(Boolean),
);

onWeAlsoRecommendArticlesClick$.subscribe(triggerRecomendedArticlesClick);

// Observers to Metro Specific Section Events Logic
const onMetroSpecificArticleClick$ = bodyClick$.pipe(
  filter(isMetroSpecificSection),
  map(getLinkableArticleElement),
  filter(Boolean),
  map((articleElement) => ({
    sectionName: 'Metro Specific Subject',
    article: articleElement,
  })),
);

onMetroSpecificArticleClick$.subscribe(triggerArticleClick);

// Observers to Listing For You Section Events Logic
const cardClickEvent$ = listingForYouSectionClick$.pipe(
  filter((event) => isStandardCard(event) && !isFavoriteIcon(event) && !isCallToAction(event)),
  map((event) => ({
    eventName: 'card_clicked',
    clickTarget: getAreaClickedName(event.target),
    card: getStandardCardElement(event),
  })),
);

const favoriteIconClickEvent$ = listingForYouSectionClick$.pipe(
  filter((event) => isFavoriteIcon(event)),
  map((event) => ({
    eventName: 'saved',
    clickTarget: getAreaClickedName(event.target),
    card: getStandardCardElement(event),
  })),
);

const standardCardClickEvent$ = merge(cardClickEvent$, favoriteIconClickEvent$);

const standardSpecCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isSpecStandardCard(data.card.dataset)),
  map((data) => ({
    customProperties: getPaidSpecCardProperties(data),
    name: data.eventName,
  })),
);

const standardPlanCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isPlanStandardCard(data.card.dataset)),
  map((data) => ({
    customProperties: getPaidPlanCardProperties(data),
    name: data.eventName,
  })),
);

const standardCommunityCardClick$ = standardCardClickEvent$.pipe(
  filter((data) => isCommunityStandardCard(data.card.dataset)),
  map((data) => ({
    customProperties: getPaidCommunityCardProperties(data),
    name: data.eventName,
  })),
);

const standardCardClickMerged$ = merge(standardSpecCardClick$, standardPlanCardClick$, standardCommunityCardClick$);
standardCardClickMerged$.subscribe(triggerListingCardClick);

// Observers to Lead Button Clicked Event Logic
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
  filter(isCallToAction),
  map(getStandardCardElement),
  filter(Boolean),
);
const callToActionSpecClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidSpecCustomProperties),
);

const callToActionPlanClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidPlanCustomProperties),
);

const callToActionCommunityClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidCommunityCustomProperties),
);

merge(callToActionSpecClick$, callToActionPlanClick$, callToActionCommunityClick$).subscribe(
  triggerLeadButtonClickedEvent,
);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
  filter(selectFetchingModal),
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
  map((modalInformation) => ({
    listingInformation: modalInformation.listingInformation,
    url: modalInformation.url,
  })),
);

const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidSpecCustomProperties),
);

const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidPlanCustomProperties),
);

const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalSubmittedPaidCommunityCustomProperties),
);

merge(modalSubmittedSpecClick$, modalSubmittedPlanClick$, modalSubmittedCommunityClick$).subscribe(
  triggerModalSubmittedEvent,
);

// Observers to ModalSubmitted Event Logic
const recommendedSubmitted$ = store$.pipe(
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.previousName === 'recommendedScreen'),
  filter((modalInformation) => Boolean(modalInformation.selectedRecommendedCommunities)),
  distinctUntilChanged(),
);

recommendedSubmitted$.subscribe(getRecommendedSubmittedPaidCommunityCustomProperties);

const modalCloseButtonClicked$ = fromEvent(document.body, 'modalClosed').pipe(
  map((event) => event?.detail?.modalInformation),
  filter(Boolean),
);

const modalCloseSpecClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isSpecStandardCard(data.listingInformation)),
  map(getModalClosePaidSpecCustomProperties),
);

const modalClosePlanClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isPlanStandardCard(data.listingInformation)),
  map(getModalClosePaidPlanCustomProperties),
);

const modalCloseCommunityClick$ = modalCloseButtonClicked$.pipe(
  filter((data) => isCommunityStandardCard(data.listingInformation)),
  map(getModalClosePaidCommunityCustomProperties),
);

merge(modalCloseSpecClick$, modalClosePlanClick$, modalCloseCommunityClick$).subscribe(triggermodalCloseEvent);
